import { baseApiClient as api } from "../baseService";
export const addTagTypes = ["smarty"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      smartyPropertyLookup: build.query<
        SmartyPropertyLookupApiResponse,
        SmartyPropertyLookupApiArg
      >({
        query: (queryArg) => ({
          url: `/api/smarty/property/`,
          params: {
            city: queryArg.city,
            freeform: queryArg.freeform,
            state: queryArg.state,
            street: queryArg.street,
            zipcode: queryArg.zipcode,
          },
        }),
        providesTags: ["smarty"],
      }),
      smartyPropertyReportPdf: build.mutation<
        SmartyPropertyReportPdfApiResponse,
        SmartyPropertyReportPdfApiArg
      >({
        query: (queryArg) => ({
          url: `/api/smarty/property/report-pdf/`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["smarty"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type SmartyPropertyLookupApiResponse =
  /** status 200  */ PropertyDataResultsResponse;
export type SmartyPropertyLookupApiArg = {
  /** City name */
  city?: string;
  /** Full address string (e.g., '123 Main St, City, ST 12345'). Use this OR individual address components. */
  freeform?: string;
  /** State abbreviation (e.g., 'CA', 'NY') */
  state?: string;
  /** Street address (e.g., '123 Main St') */
  street?: string;
  /** ZIP code */
  zipcode?: string;
};
export type SmartyPropertyReportPdfApiResponse = /** status 200  */ any;
export type SmartyPropertyReportPdfApiArg = {
  body: {
    /** HTML content to convert to PDF */
    html: string;
    /** Filename for the downloaded PDF (without .pdf extension) */
    filename?: string;
  };
};
export type PropertyMatchedAddress = {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
};
export type PropertyAttributes = {
  /** Unique parcel identifier. Example: '123-456-789' */
  parcel_id?: string | null;
  /** Assessor's parcel account number. Example: '1-00834-0039', '3-02017-0061' */
  parcel_account_number?: string | null;
  /** Raw parcel number as recorded. Example: '01275-0027' */
  parcel_raw_number?: string | null;
  /** Alternate parcel number. Example: '17-10-137-014-0000' */
  parcel_number_alternate?: string | null;
  /** Previous parcel number. Example: '0503012750027' */
  parcel_number_previous?: string | null;
  /** Year parcel was added. Example: '1995', '2008' */
  parcel_number_year_added?: string | null;
  /** Year parcel number changed. Example: '2005', '2014' */
  parcel_number_year_change?: string | null;
  /** FIPS code for the county. Example: '36061', '48453' */
  fips_code?: string | null;
  /** Subdivision name. Example: 'Block 005 Original City', 'Carne Humana Rancho' */
  subdivision?: string | null;
  /** Legal property description. Example: 'Lot:39 Blk:834 Sec:306 Dist:05' */
  legal_description?: string | null;
  /** Match type: 'parcel' or 'sub_parcel' */
  match_type?: string | null;
  /** Lot size in acres. Example: '0.2410468', '114.3200000' */
  acres?: string | null;
  /** Lot size in square feet. Example: '10500.00', '44213.00' */
  lot_sqft?: string | null;
  /** Lot depth in feet. Example: '123.5', '141.4' */
  depth_linear_footage?: string | null;
  /** Lot width in feet. Example: '81.8', '20.0' */
  width_linear_footage?: string | null;
  /** Block identifier. Example: '834', '2017', '5' */
  block1?: string | null;
  /** Primary lot number. Example: '39', '61', '1-6' */
  lot_1?: string | null;
  /** Secondary lot number */
  lot_2?: string | null;
  /** PLSS section number. Example: '03', '07', '26' */
  section?: string | null;
  /** PLSS township. Example: '06', '04', '03S' */
  township?: string | null;
  /** PLSS range. Example: '29', '68W' */
  range?: string | null;
  /** Quarter section. Example: 'NW' */
  quarter?: string | null;
  /** Tract number. Example: '7352', '99' */
  tract_number?: string | null;
  /** Terrain type. Example: 'level' */
  topography_code?: string | null;
  /** Year structure was built. Example: '1905', '1899', '1985' */
  year_built?: string | null;
  /** Effective year built after renovations. Example: '2019' */
  effective_year_built?: string | null;
  /** Number of stories. Example: '11', '3', '5' */
  stories_number?: string | null;
  /** Number of bedrooms. Example: '3' */
  bedrooms?: string | null;
  /** Number of bathrooms */
  bathrooms?: string | null;
  /** Total bathrooms including partial. Example: '2.000' */
  bathrooms_total?: string | null;
  /** Total number of rooms. Example: '5', '75' */
  rooms?: string | null;
  /** Building square footage. Example: '99922', '3660', '441232' */
  building_sqft?: string | null;
  /** Gross square footage. Example: '99922', '441232' */
  gross_sqft?: string | null;
  /** Living space square footage */
  living_sqft?: string | null;
  /** Basement square footage. Example: '2880' */
  basement_sqft?: string | null;
  /** Finished basement square footage. Example: '2880' */
  basement_sqft_finished?: string | null;
  /** Garage square footage. Example: '71112' */
  garage_sqft?: string | null;
  /** Number of parking spaces. Example: '0', '662' */
  parking_spaces?: string | null;
  /** Number of units in building. Example: '49', '3' */
  unit_count?: string | null;
  /** Number of buildings on property. Example: '1' */
  number_of_buildings?: string | null;
  /** How sqft is measured: 'gross_area' or 'total_area' */
  building_definition_code?: string | null;
  /** First floor sqft. API field: '1st_floor_sqft'. Example: '27985' */
  first_floor_sqft?: string | null;
  /** Second floor sqft. API field: '2nd_floor_sqft'. Example: '28375' */
  second_floor_sqft?: string | null;
  /** Architectural style */
  building_style?: string | null;
  /** Construction material. Example: 'concrete', 'wood' */
  construction_type?: string | null;
  /** Exterior wall material */
  exterior_wall?: string | null;
  /** Exterior walls. Example: 'glass', 'brick' */
  exterior_walls?: string | null;
  /** Roof structure type */
  roof_type?: string | null;
  /** Roof covering. Example: 'other_not_classified' */
  roof_cover?: string | null;
  /** Roof frame type. Example: 'wood_truss' */
  roof_frame?: string | null;
  /** Foundation type. Example: 'type_not_specified' */
  foundation?: string | null;
  /** Interior wall type. Example: 'wallboard_drywall' */
  interior_structure?: string | null;
  /** Fire resistance. Example: 'fireproof_steel_frames', 'wood_or_wood_and_steel_frames' */
  fire_resistance_code?: string | null;
  /** Heating type. Example: 'none', 'central' */
  heat?: string | null;
  /** Heating fuel. Example: 'electric' */
  heat_fuel_type?: string | null;
  /** AC presence/type */
  air_conditioning?: string | null;
  /** AC type. Example: 'central', 'ac_package' */
  air_conditioner?: string | null;
  /** Fireplace presence. Example: 'yes' */
  fireplace?: string | null;
  /** Number of fireplaces. Example: '0', '1' */
  fireplace_number?: string | null;
  /** Pool presence/type. Example: 'hot_tub' */
  pool?: string | null;
  /** Deck presence. Example: 'deck_present' */
  deck?: string | null;
  /** Deck square footage. Example: '1900' */
  deck_area?: string | null;
  /** Porch type. Example: 'open_porch' */
  porch?: string | null;
  /** Porch square footage */
  porch_area?: string | null;
  /** Garage type. Example: 'garage_basement', 'garage_attached' */
  garage?: string | null;
  /** Sprinkler system. Example: 'sprinklers_exist' */
  sprinklers?: string | null;
  /** Number of plumbing fixtures */
  plumbing_fixtures_count?: string | null;
  /** Elevator presence. Example: 'elevator_present' */
  elevator?: string | null;
  /** Escalator presence. Example: 'escalator_present' */
  escalator?: string | null;
  /** Office space presence. Example: 'office_present' */
  office?: string | null;
  /** Office square footage. Example: '90922' */
  office_sqft?: string | null;
  /** Canopy presence. Example: 'canopy_present' */
  canopy?: string | null;
  /** Canopy square footage */
  canopy_sqft?: string | null;
  /** Utility space. Example: 'utility_present' */
  utility?: string | null;
  /** Utility space square footage */
  utility_sqft?: string | null;
  /** Loading platform. Example: 'loading_platform_present' */
  loading_platform?: string | null;
  /** Loading platform sqft. Example: '3150' */
  loading_platform_sqft?: string | null;
  /** Property use code */
  property_use_code?: string | null;
  /** Property use description */
  property_use_description?: string | null;
  /** Land use code. Example: 'O6', 'C0', '50' */
  land_use_code?: string | null;
  /** Land use description */
  land_use_description?: string | null;
  /** Land use category. Example: 'commercial', 'residential', 'industrial', 'agriculture_farming' */
  land_use_group?: string | null;
  /** Standardized land use. Example: 'financial_building', 'triplex_3_units_any_combination', 'skyscraper_highrise_commercial' */
  land_use_standard?: string | null;
  /** Zoning classification. Example: 'C5-2', 'R6b', 'AP' */
  zoning?: string | null;
  /** Total assessed value. Example: '14104190', '44659' */
  assessed_value?: string | null;
  /** Assessed land value. Example: '4437000', '8577' */
  assessed_land_value?: string | null;
  /** Assessed improvement value. Example: '9667190', '36082' */
  assessed_improvement_value?: string | null;
  /** Improvement percent. Example: '68.00', '80.00' */
  assessed_improvement_percent?: string | null;
  /** Prior year assessed value. Example: '14933840' */
  previous_assessed_value?: string | null;
  /** Total market value. Example: '32115000', '2239000', '179485651' */
  total_market_value?: string | null;
  /** Market land value. Example: '9860000', '430000' */
  market_land_value?: string | null;
  /** Market improvement value. Example: '22255000', '1809000' */
  market_improvement_value?: string | null;
  /** Market improvement percent. Example: '69.00', '80.00' */
  market_improvement_percent?: string | null;
  /** Year of market valuation. Example: '2025' */
  market_value_year?: string | null;
  /** Annual tax amount. Example: '1517892.93', '8969.76', '5128971.50' */
  tax_billed_amount?: string | null;
  /** Assessment year. Example: '2025' */
  tax_assess_year?: string | null;
  /** Tax fiscal year. Example: '2025', '2024' */
  tax_fiscal_year?: string | null;
  /** Tax rate area. Example: '4', '1', '02 2j 2u 68' */
  tax_rate_area?: string | null;
  /** Tax jurisdiction. Example: 'New York(Manhattan)', 'Kings(Brooklyn)', 'Travis' */
  tax_jurisdiction?: string | null;
  /** Homeowner exemption. Example: 'exemption_filed' */
  homeowner_tax_exemption?: string | null;
  /** Senior exemption. Example: 'exemption_filed' */
  senior_tax_exemption?: string | null;
  /** Veteran exemption. Example: 'exemption_filed' */
  veteran_tax_exemption?: string | null;
  /** Other tax exemptions. Example: '1' */
  other_tax_exemption?: string | null;
  /** Owner name */
  owner_name?: string | null;
  /** Owner full name. Example: '320 Realty CO', 'Bvt13, LLC' */
  owner_full_name?: string | null;
  /** Owner first name */
  first_name?: string | null;
  /** Owner middle name */
  middle_name?: string | null;
  /** Owner last name. Example: '320 Realty CO', 'Bvt13, LLC' */
  last_name?: string | null;
  /** Occupancy status. Example: 'not_owner_occupied', 'owner_occupied' */
  owner_occupancy_status?: string | null;
  /** Ownership type. Example: 'company' */
  ownership_type?: string | null;
  /** Company indicator. Example: 'owner_is_company', 'owner_is_not_a_company' */
  company_flag?: string | null;
  /** Trust status. Example: 'not_a_trust' */
  trust_description?: string | null;
  /** Second owner first name */
  first_name_2?: string | null;
  /** Second owner middle name */
  middle_name_2?: string | null;
  /** Second owner last name */
  last_name_2?: string | null;
  /** Second owner full name. Example: '601 Travis Owner LLC' */
  owner_full_name_2?: string | null;
  /** Second owner type. Example: 'company' */
  ownership_type_2?: string | null;
  /** Third owner first name */
  first_name_3?: string | null;
  /** Third owner middle name */
  middle_name_3?: string | null;
  /** Third owner last name */
  last_name_3?: string | null;
  /** Third owner full name. Example: 'Chase Tower' */
  owner_full_name_3?: string | null;
  /** Deed owner full name */
  deed_owner_full_name?: string | null;
  /** Deed owner first name */
  deed_owner_first_name?: string | null;
  /** Deed owner middle name */
  deed_owner_middle_name?: string | null;
  /** Deed owner last name */
  deed_owner_last_name?: string | null;
  /** Second deed owner first name (no underscore in API) */
  deed_owner_first_name2?: string | null;
  /** Second deed owner last name (no underscore in API) */
  deed_owner_last_name2?: string | null;
  /** Second deed owner full name (no underscore in API) */
  deed_owner_full_name2?: string | null;
  /** Sale date (YYYY-MM-DD). Example: '2021-02-19', '2016-10-20' */
  sale_date?: string | null;
  /** Sale price. Example: '1425000' */
  sale_amount?: string | null;
  /** Prior sale date. Example: '2019-08-19', '1996-03-12' */
  prior_sale_date?: string | null;
  /** Prior sale price. Example: '1500000' */
  prior_sale_amount?: string | null;
  /** Deed sale date */
  deed_sale_date?: string | null;
  /** Deed sale price */
  deed_sale_price?: string | null;
  /** Transfer amount. Example: '1425000', '510000' */
  transfer_amount?: string | null;
  /** Instrument/deed date. Example: '2021-01-29' */
  instrument_date?: string | null;
  /** Recording date. Example: '2021-02-19', '2018-02-06' */
  recording_date?: string | null;
  /** Ownership transfer date. Example: '2021-02-19' */
  ownership_transfer_date?: string | null;
  /** Document type. Example: 'deed', 'special_warranty_deed' */
  document_type_description?: string | null;
  /** Deed document number. Example: '2021000061622', '2016175748' */
  deed_document_number?: string | null;
  /** Deed book number. Example: '34840' */
  deed_document_book?: string | null;
  /** Deed page number. Example: '3343' */
  deed_document_page?: string | null;
  /** Internal transaction ID. Example: '951323013' */
  deed_transaction_id?: string | null;
  /** Transfer document number. Example: '0000061622' */
  ownership_transfer_doc_number?: string | null;
  /** Transfer transaction ID. Example: '951323013' */
  ownership_transfer_transaction_id?: string | null;
  /** Title company code. Example: '26021' */
  code_title_company?: string | null;
  /** Title company name. Example: 'None Available' */
  name_title_company?: string | null;
  /** Mortgage amount. Example: '408000', '2500' */
  mortgage_amount?: string | null;
  /** Mortgage maturity date. Example: '2055-07-01' */
  mortgage_due_date?: string | null;
  /** Mortgage recording date. Example: '2018-02-06' */
  mortgage_recording_date?: string | null;
  /** Loan term. Example: '360' */
  mortgage_term?: string | null;
  /** Term unit. Example: 'month' */
  mortgage_term_type?: string | null;
  /** Loan type. Example: 'conventional', 'commercial' */
  mortgage_type?: string | null;
  /** Interest rate type. Example: 'fixed_rate', 'adjustable_rate' */
  mortgage_interest_rate_type?: string | null;
  /** Lender code. Example: '508273' */
  mortgage_lender_code?: string | null;
  /** Second mortgage lender code */
  lender_code_2?: string | null;
  /** Seller carryback. Example: 'non_seller_carryback_loan' */
  lender_seller_carry_back?: string | null;
  /** Lender name. Example: 'Newrez LLC', 'Hunt Communities Bastrop LLC' */
  lender_name?: string | null;
  /** Lender last name (same as lender_name for companies) */
  lender_last_name?: string | null;
  /** Lender address */
  lender_address?: string | null;
  /** Lender city */
  lender_city?: string | null;
  /** Lender state. Example: 'CA', 'NC' */
  lender_state?: string | null;
  /** Lender ZIP */
  lender_zip?: string | null;
  /** Lender ZIP+4 */
  lender_zip_extended?: string | null;
  /** Full mailing address. Example: '52 Vanderbilt Ave', '15 Brevoort PL' */
  contact_full_address?: string | null;
  /** Mailing house number. Example: '52', '15' */
  contact_house_number?: string | null;
  /** Mailing pre-direction. Example: 'N', 'NE' */
  contact_pre_direction?: string | null;
  /** Mailing post-direction */
  contact_post_direction?: string | null;
  /** Mailing street name. Example: 'Vanderbilt', 'Brevoort' */
  contact_street_name?: string | null;
  /** Mailing street suffix. Example: 'Ave', 'PL', 'Fwy' */
  contact_suffix?: string | null;
  /** Mailing unit type. Example: 'Ste', '#' */
  contact_unit_designator?: string | null;
  /** Mailing unit value. Example: '1310STE131' */
  contact_value?: string | null;
  /** Mailing city. Example: 'New York', 'Brooklyn', 'Dallas' */
  contact_city?: string | null;
  /** Mailing state. Example: 'NY', 'TX' */
  contact_state?: string | null;
  /** Mailing ZIP. Example: '10017', '11216' */
  contact_zip?: string | null;
  /** Mailing ZIP+4. Example: '3808', '1811' */
  contact_zip4?: string | null;
  /** Carrier route code. Example: 'C049', 'C022' */
  contact_crrt?: string | null;
  /** Mailing county. Example: 'New York', 'Kings', 'Dallas' */
  contact_mailing_county?: string | null;
  /** Mailing county FIPS. Example: '36061', '36047' */
  contact_mailing_fips?: string | null;
  /** Mail format. Example: 'highrise', 'standard_us' */
  contact_mail_info_format?: string | null;
  /** Full property address. Example: '320 5th Ave # 324' */
  property_address_full?: string | null;
  /** Property house number. Example: '320' */
  property_address_house_number?: string | null;
  /** Property pre-direction */
  property_address_pre_direction?: string | null;
  /** Property post-direction */
  property_address_post_direction?: string | null;
  /** Property street name. Example: '5th', 'Brevoort' */
  property_address_street_name?: string | null;
  /** Property street suffix. Example: 'Ave', 'PL' */
  property_address_street_suffix?: string | null;
  /** Property unit type. Example: '#' */
  property_address_unit_designator?: string | null;
  /** Property unit value. Example: '324' */
  property_address_unit_value?: string | null;
  /** Property city. Example: 'New York', 'Brooklyn', 'Austin' */
  property_address_city?: string | null;
  /** Property state. Example: 'NY', 'TX' */
  property_address_state?: string | null;
  /** Property ZIP. Example: '10001', '11216', '78701' */
  property_address_zipcode?: string | null;
  /** Property ZIP+4. Example: '3102', '1892', '4072' */
  property_address_zip_4?: string | null;
  /** Property carrier route. Example: 'C019', 'C022', 'C012' */
  property_address_carrier_route_code?: string | null;
  /** Property latitude. Example: '40.747473', '40.680307' */
  latitude?: string | null;
  /** Property longitude. Example: '-73.985724', '-73.954907' */
  longitude?: string | null;
  /** Elevation in feet. Example: '46', '69', '472' */
  elevation_feet?: string | null;
  /** Property county. Example: 'New York', 'Kings', 'Travis' */
  situs_county?: string | null;
  /** Property state. Example: 'NY', 'TX' */
  situs_state?: string | null;
  /** Census tract. Example: '7600', '22700', '1102' */
  census_tract?: string | null;
  /** Census block. Example: '1003', '2000', '2010' */
  census_block?: string | null;
  /** Census block group. Example: '1', '2' */
  census_block_group?: string | null;
  /** Census FIPS place code. Example: '51000', '05000' */
  census_fips_place_code?: string | null;
  /** Congressional district. Example: '302', '276', '387' */
  congressional_district?: string | null;
  /** Neighborhood code. Example: 'BD', '28' */
  neighborhood_code?: string | null;
  /** CBSA code. Example: '35620', '12420' */
  cbsa_code?: string | null;
  /** CBSA name. Example: 'New York-Northern New Jersey-Long Island, NY-NJ-PA Metropolitan Statistical Area' */
  cbsa_name?: string | null;
  /** MSA code. Example: '35620', '12420' */
  msa_code?: string | null;
  /** MSA name. Example: 'New York-Northern New Jersey-Long Island, NY-NJ-PA' */
  msa_name?: string | null;
  /** Metro division. Example: 'New York-White Plains-Wayne, NY-NJ Metropolitan Division' */
  metro_division?: string | null;
  /** Combined statistical area. Example: 'New York-Newark, NY-NJ-CT-PA' */
  combined_statistical_area?: string | null;
  /** MCD code. Example: '44919', '10022', '90165' */
  minor_civil_division_code?: string | null;
  /** MCD name. Example: 'Manhattan', 'Brooklyn', 'Austin' */
  minor_civil_division_name?: string | null;
  /** Multi-parcel indicator. Example: 'multiple_parcels_conveyed_unknown_impact_to_apn' */
  multi_parcel_flag?: string | null;
  /** Last assessor update. Example: '2025-06-23' */
  assessor_last_update?: string | null;
  /** Tax roll update date. Example: '2025-05-31' */
  assessor_taxroll_update?: string | null;
  /** Data publication date. Example: '2025-07-03', '2025-11-05' */
  publication_date?: string | null;
  /** Additional attributes not explicitly modeled in the schema */
  extra_attributes?: {
    [key: string]: string;
  };
  [key: string]: any;
};
export type PropertyDataResult = {
  smarty_key: string;
  data_set_name: string;
  data_subset_name?: string | null;
  matched_address: PropertyMatchedAddress;
  attributes: PropertyAttributes;
};
export type PropertyDataResultsResponse = {
  results: PropertyDataResult[];
};
export type PropertyDataError = {
  error: string;
  error_code: string;
};
export const {
  useSmartyPropertyLookupQuery,
  useSmartyPropertyReportPdfMutation,
} = injectedRtkApi;
